import React, { useState, useEffect, useMemo } from "react";
import {
  TableContainer,
  Paper,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Box,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
  InputAdornment,
} from "@mui/material";
import { Search, Edit, Trash2, Plus, Euro, DollarSign } from 'lucide-react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  createColumnHelper,
  flexRender,
} from "@tanstack/react-table";
import api from '../utils/api';

const DepensesPage = () => {
  // États pour les données
  const [depenses, setDepenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  // États pour le tableau
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });
  const [sorting, setSorting] = useState([{ id: "date_debut", desc: true }]);
  const [columnOrder, setColumnOrder] = useState(() => {
    const savedOrder = localStorage.getItem('depensesColumnOrder');
    return savedOrder ? JSON.parse(savedOrder) : [];
  });
  const [columnSizing, setColumnSizing] = useState(() => {
    const savedSizing = localStorage.getItem('depensesColumnSizing');
    if (savedSizing) {
      return JSON.parse(savedSizing);
    }
    return {
      description: 250,
      categorie: 150,
      montant: 120,
      montant_ht: 120,
      monnaie: 80,
      taux_tva: 100,
      montant_tva: 120,
      est_recurrent: 120,
      frequence: 150,
      date_debut: 120,
      date_fin: 120,
      actions: 120,
    };
  });

  // États pour le formulaire
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    description: "",
    categorie_id: "",
    montant: "",
    monnaie: "EUR",
    taux_tva: 20,
    tva_deductible: true,
    est_recurrent: false,
    frequence: "",
    date_debut: "",
    date_fin: "",
  });
  const [editingId, setEditingId] = useState(null);

  // État pour la notification
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // États pour les filtres de date
  const [dateFilter, setDateFilter] = useState('thisMonth');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [monthlyTotals, setMonthlyTotals] = useState([]);

  // Ajouter le columnHelper avant columns
  const columnHelper = createColumnHelper();
  const columns = useMemo(
    () => [
      columnHelper.accessor("description", {
        header: "Description",
        cell: (info) => info.getValue(),
        enableColumnFilter: true,
        enableSorting: true,
        size: columnSizing.description,
        enableResizing: true,
      }),
      columnHelper.accessor("categorie_id", {
        header: "Catégorie",
        cell: (info) => {
          const categorie = categories.find((c) => c.id === info.getValue());
          return categorie ? categorie.nom : "-";
        },
        enableColumnFilter: true,
        enableSorting: true,
        size: columnSizing.categorie,
        enableResizing: true,
      }),
      columnHelper.accessor("montant", {
        header: "Montant TTC",
        cell: (info) => {
          const montant = Number(info.row.original.montant);
          const montantTVA = Number(info.row.original.montant_tva);
          const monnaie = info.row.original.monnaie;
          return `${formatAmount(montant + montantTVA)} ${monnaie}`;
        },
        enableColumnFilter: true,
        enableSorting: true,
        size: columnSizing.montant,
        enableResizing: true,
      }),
      columnHelper.display({
        id: "montant_ht",
        header: "Montant HT",
        cell: (info) => {
          const montant = info.row.original.montant;
          const monnaie = info.row.original.monnaie;
          return `${formatAmount(montant)} ${monnaie}`;
        },
        size: columnSizing.montant_ht,
        enableResizing: true,
      }),
      columnHelper.accessor("taux_tva", {
        header: "Taux TVA",
        cell: (info) => `${info.getValue()}%`,
        enableColumnFilter: true,
        enableSorting: true,
        size: columnSizing.taux_tva,
        enableResizing: true,
      }),
      columnHelper.accessor("montant_tva", {
        header: "Montant TVA",
        cell: (info) => {
          const montant = info.getValue();
          const monnaie = info.row.original.monnaie;
          return `${formatAmount(montant)} ${monnaie}`;
        },
        enableColumnFilter: true,
        enableSorting: true,
        size: columnSizing.montant_tva,
        enableResizing: true,
      }),
      columnHelper.accessor("est_recurrent", {
        header: "Récurrent",
        cell: (info) => (info.getValue() ? "Oui" : "Non"),
        enableColumnFilter: true,
        enableSorting: true,
        size: columnSizing.est_recurrent,
        enableResizing: true,
      }),
      columnHelper.accessor("frequence", {
        header: "Fréquence",
        cell: (info) => info.getValue() || "-",
        enableColumnFilter: true,
        enableSorting: true,
        size: columnSizing.frequence,
        enableResizing: true,
      }),
      columnHelper.accessor("occurrence_date", {
        header: "Date",
        cell: (info) => formatDate(info.getValue()),
        enableColumnFilter: true,
        enableSorting: true,
        size: columnSizing.date_debut,
        enableResizing: true,
      }),
      columnHelper.accessor("date_fin", {
        header: "Date fin",
        cell: (info) => formatDate(info.getValue()),
        enableColumnFilter: true,
        enableSorting: true,
        size: columnSizing.date_fin,
        enableResizing: true,
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Modifier">
              <IconButton
                size="small"
                onClick={() => handleOpenDialog(info.row.original)}
              >
                <Edit size={16} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Supprimer">
              <IconButton
                size="small"
                onClick={() => handleDelete(info.row.original.id)}
              >
                <Trash2 size={16} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
        size: columnSizing.actions,
        enableResizing: true,
      }),
    ],
    [categories, columnSizing]
  );

  // Chargement des données
  const fetchDepenses = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/depenses");
      setDepenses(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      showNotification("Erreur lors du chargement des dépenses", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await api.get("/api/depenses/categories");
      setCategories(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      showNotification("Erreur lors du chargement des catégories", "error");
    }
  };

  useEffect(() => {
    fetchDepenses();
    fetchCategories();
  }, []);

  // Sauvegarde de la configuration des colonnes
  useEffect(() => {
    if (columnOrder.length > 0) {
      localStorage.setItem('depensesColumnOrder', JSON.stringify(columnOrder));
    }
  }, [columnOrder]);

  useEffect(() => {
    if (Object.keys(columnSizing).length > 0) {
      localStorage.setItem('depensesColumnSizing', JSON.stringify(columnSizing));
    }
  }, [columnSizing]);

  // Fonctions utilitaires
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR');
  };

  const formatAmount = (amount) =>
    amount !== undefined && amount !== null && !isNaN(amount)
      ? Number(amount).toFixed(2)
      : "-";

  const showNotification = (message, severity = "success") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  // Gestion du formulaire
  const handleOpenDialog = (depense = null) => {
    if (depense) {
      setFormData({
        description: depense.description,
        categorie_id: depense.categorie_id,
        montant: (Number(depense.montant) + Number(depense.montant_tva)).toFixed(2),
        monnaie: depense.monnaie,
        taux_tva: depense.taux_tva,
        tva_deductible: depense.tva_deductible,
        est_recurrent: depense.est_recurrent,
        frequence: depense.frequence,
        date_debut: depense.date_debut,
        date_fin: depense.date_fin,
      });
      setEditingId(depense.id);
    } else {
      setFormData({
        description: "",
        categorie_id: "",
        montant: "",
        monnaie: "EUR",
        taux_tva: 20,
        tva_deductible: true,
        est_recurrent: false,
        frequence: "",
        date_debut: "",
        date_fin: "",
      });
      setEditingId(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { montant, taux_tva, tva_deductible } = formData;
    let montantHT = Number(montant);
    let montantTVA = 0;

    if (tva_deductible && Number(taux_tva) > 0) {
      const taux = Number(taux_tva) / 100;
      montantHT = Number(montant) / (1 + taux);
      montantTVA = montantHT * taux;
    }
    const dataToSend = { ...formData, montant: montantHT, montant_tva: montantTVA };

    try {
      if (editingId) {
        await api.put(`/api/depenses/${editingId}`, dataToSend);
        showNotification("Dépense mise à jour avec succès");
      } else {
        await api.post("/api/depenses", dataToSend);
        showNotification("Dépense créée avec succès");
      }
      fetchDepenses();
      handleCloseDialog();
    } catch (err) {
      console.error(err);
      showNotification(
        `Erreur lors de ${editingId ? "la mise à jour" : "la création"} de la dépense`,
        "error"
      );
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette dépense ?")) {
      try {
        await api.delete(`/api/depenses/${id}`);
        showNotification("Dépense supprimée avec succès");
        fetchDepenses();
      } catch (err) {
        console.error(err);
        showNotification("Erreur lors de la suppression de la dépense", "error");
      }
    }
  };

  // Générer les occurrences de dépenses récurrentes selon la période filtrée
  const generateRecurrentOccurrences = (depense, startDate, endDate) => {
    const occurrences = [];
    if (!depense.est_recurrent) return occurrences;
    const freq = depense.frequence;
    let current = new Date(depense.date_debut);
    const last = depense.date_fin ? new Date(depense.date_fin) : endDate;
    // S'assurer que la première occurrence est dans la période
    if (current < startDate) {
      // Avancer jusqu'à la première occurrence >= startDate
      while (current < startDate) {
        switch (freq) {
          case '1 semaine': current.setDate(current.getDate() + 7); break;
          case '2 semaines': current.setDate(current.getDate() + 14); break;
          case '1 mois': current.setMonth(current.getMonth() + 1); break;
          case '3 mois': current.setMonth(current.getMonth() + 3); break;
          case '6 mois': current.setMonth(current.getMonth() + 6); break;
          case '1 an': current.setFullYear(current.getFullYear() + 1); break;
          default: current.setMonth(current.getMonth() + 1);
        }
      }
    }
    // Générer les occurrences dans la période
    while (current <= endDate && current <= last) {
      occurrences.push({ ...depense, occurrence_date: new Date(current) });
      // Avancer à la prochaine occurrence
      switch (freq) {
        case '1 semaine': current.setDate(current.getDate() + 7); break;
        case '2 semaines': current.setDate(current.getDate() + 14); break;
        case '1 mois': current.setMonth(current.getMonth() + 1); break;
        case '3 mois': current.setMonth(current.getMonth() + 3); break;
        case '6 mois': current.setMonth(current.getMonth() + 6); break;
        case '1 an': current.setFullYear(current.getFullYear() + 1); break;
        default: current.setMonth(current.getMonth() + 1);
      }
    }
    return occurrences;
  };

  // Fonction pour obtenir les dates de début et fin en fonction du filtre
  const getDateRange = () => {
    const now = new Date();
    let startDate, endDate;
    switch (dateFilter) {
      case 'all':
        return { startDate: new Date(2000, 0, 1), endDate: new Date(2100, 0, 1) };
      case 'thisMonth':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'lastMonth':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'thisYear':
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'custom':
        startDate = customStartDate;
        endDate = customEndDate;
        break;
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    }
    return { startDate, endDate };
  };

  // Fonction pour filtrer et aplatir les dépenses (récurrentes et non récurrentes)
  const getFlattenedDepenses = () => {
    const { startDate, endDate } = getDateRange();
    let result = [];
    depenses.forEach(depense => {
      if (depense.est_recurrent) {
        result = result.concat(generateRecurrentOccurrences(depense, startDate, endDate));
      } else {
        const dateDebut = new Date(depense.date_debut);
        if ((!startDate || dateDebut >= startDate) && (!endDate || dateDebut <= endDate)) {
          result.push({ ...depense, occurrence_date: dateDebut });
        }
      }
    });
    // Filtrer selon la recherche globale
    if (globalFilter) {
      result = result.filter(d =>
        d.description?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        categories.find(c => c.id === d.categorie_id)?.nom?.toLowerCase().includes(globalFilter.toLowerCase())
      );
    }
    // Trier par date décroissante
    return result.sort((a, b) => b.occurrence_date - a.occurrence_date);
  };

  // Mise à jour du tableau avec les dépenses aplaties
  const filteredDepenses = useMemo(() => getFlattenedDepenses(), [depenses, dateFilter, customStartDate, customEndDate, globalFilter]);

  // Configuration du tableau
  const table = useReactTable({
    data: filteredDepenses,
    columns,
    state: {
      globalFilter,
      pagination,
      sorting,
      columnOrder,
      columnSizing,
    },
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: (newSizing) => {
      setColumnSizing(newSizing);
      localStorage.setItem('depensesColumnSizing', JSON.stringify(newSizing));
    },
    globalFilterFn: "includesString",
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableColumnResizing: true,
    enableColumnOrdering: true,
    columnResizeMode: "onChange",
    defaultColumn: {
      minSize: 50,
      maxSize: 500,
      size: 150,
    },
  });

  // Charger les totaux par période
  const fetchTotals = async () => {
    try {
      const { startDate, endDate } = getDateRange();
      if (!startDate || !endDate) return;

      const params = new URLSearchParams({
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0]
      });
      
      const res = await api.get(`/api/depenses/totals?${params}`);
      setMonthlyTotals(res.data);
    } catch (err) {
      console.error(err);
      showNotification("Erreur lors du chargement des totaux", "error");
    }
  };

  useEffect(() => {
    fetchTotals();
  }, [dateFilter, customStartDate, customEndDate]);

  // Fonction pour obtenir les dépenses récurrentes actives
  const getActiveRecurrentDepenses = () => {
    return depenses.filter(depense => 
      depense.est_recurrent && 
      (!depense.date_fin || new Date(depense.date_fin) > new Date())
    );
  };

  // Fonction pour calculer le montant total des dépenses récurrentes
  const calculateRecurrentTotal = (depense) => {
    const montant = Number(depense.montant);
    const montantTVA = Number(depense.montant_tva);
    
    // Si la dépense a une date de fin, calculer le nombre de paiements jusqu'à cette date
    if (depense.date_fin) {
      const dateDebut = new Date(depense.date_debut);
      const dateFin = new Date(depense.date_fin);
      const today = new Date();
      
      // Si la date de fin est passée, ne pas inclure dans le total
      if (dateFin < today) return { total: 0, totalTVA: 0 };
      
      // Calculer le nombre de paiements en fonction de la fréquence
      let nbPaiements = 0;
      switch (depense.frequence) {
        case '1 semaine':
          nbPaiements = Math.ceil((dateFin - dateDebut) / (7 * 24 * 60 * 60 * 1000));
          break;
        case '2 semaines':
          nbPaiements = Math.ceil((dateFin - dateDebut) / (14 * 24 * 60 * 60 * 1000));
          break;
        case '1 mois':
          nbPaiements = Math.ceil((dateFin - dateDebut) / (30 * 24 * 60 * 60 * 1000));
          break;
        case '3 mois':
          nbPaiements = Math.ceil((dateFin - dateDebut) / (90 * 24 * 60 * 60 * 1000));
          break;
        case '6 mois':
          nbPaiements = Math.ceil((dateFin - dateDebut) / (180 * 24 * 60 * 60 * 1000));
          break;
        case '1 an':
          nbPaiements = Math.ceil((dateFin - dateDebut) / (365 * 24 * 60 * 60 * 1000));
          break;
        default:
          nbPaiements = 1;
      }
      
      return {
        total: montant * nbPaiements,
        totalTVA: montantTVA * nbPaiements
      };
    }
    
    // Si pas de date de fin, retourner le montant mensuel
    return { total: montant, totalTVA: montantTVA };
  };

  return (
    <div style={{ padding: "20px" }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Gestion des Dépenses
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Plus size={20} />}
          onClick={() => handleOpenDialog()}
        >
          Nouvelle dépense
        </Button>
      </Box>

      {/* Dépenses récurrentes actives (compactes) */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Dépenses récurrentes actives
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          flexWrap: 'wrap',
          mb: 3
        }}>
          {getActiveRecurrentDepenses().map((depense) => {
            const { total, totalTVA } = calculateRecurrentTotal(depense);
            return (
              <Card key={depense.id} sx={{ 
                minWidth: 220,
                maxWidth: 260,
                p: 1.5, 
                bgcolor: '#f8fafc', 
                borderRadius: 2, 
                border: '1px solid #e2e8f0',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                fontSize: '0.95em',
                boxShadow: 'none',
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1, fontSize: '1em' }}>
                    {depense.description}
                  </Typography>
                  <IconButton size="small" onClick={() => handleOpenDialog(depense)}>
                    <Edit size={16} />
                  </IconButton>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {depense.frequence} {depense.date_fin ? `- Jusqu'au ${formatDate(depense.date_fin)}` : ''}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#dc2626', fontSize: '1.1em' }}>
                    {formatAmount(total)} €
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#16a34a', fontWeight: 500 }}>
                    TVA: {formatAmount(totalTVA)} €
                  </Typography>
                </Box>
              </Card>
            );
          })}
        </Box>
      </Box>

      {/* Filtres et tableau */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          alignItems: 'center', 
          mb: 2,
          flexWrap: 'wrap'
        }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Période</InputLabel>
            <Select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              label="Période"
              size="small"
            >
              <MenuItem value="all">Tous</MenuItem>
              <MenuItem value="thisMonth">Ce mois-ci</MenuItem>
              <MenuItem value="lastMonth">Mois dernier</MenuItem>
              <MenuItem value="thisYear">Cette année</MenuItem>
              <MenuItem value="custom">Période personnalisée</MenuItem>
            </Select>
          </FormControl>

          {dateFilter === 'custom' && (
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
              <Box sx={{ display: 'inline-flex', gap: 2 }}>
                <DatePicker
                  label="Date de début"
                  value={customStartDate}
                  onChange={setCustomStartDate}
                  slotProps={{ textField: { size: 'small' } }}
                />
                <DatePicker
                  label="Date de fin"
                  value={customEndDate}
                  onChange={setCustomEndDate}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </Box>
            </LocalizationProvider>
          )}

          <TextField
            label="Recherche"
            variant="outlined"
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            size="small"
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #ccc",
                          textAlign: "left",
                          width: header.getSize(),
                          cursor: header.column.getCanSort() ? "pointer" : "default",
                          backgroundColor: '#f8fafc',
                        }}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #eee",
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
                {/* Ligne des totaux */}
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <td colSpan={2} style={{ textAlign: 'right', fontWeight: 'bold', padding: '12px 8px' }}>
                    Total
                  </td>
                  <td style={{ fontWeight: 'bold', padding: '12px 8px' }}>
                    {formatAmount(filteredDepenses.reduce((sum, d) => sum + Number(d.montant) + Number(d.montant_tva), 0))} €
                  </td>
                  <td style={{ fontWeight: 'bold', padding: '12px 8px' }}>
                    {formatAmount(filteredDepenses.reduce((sum, d) => sum + Number(d.montant), 0))} €
                  </td>
                  <td></td>
                  <td style={{ fontWeight: 'bold', padding: '12px 8px', color: '#16a34a' }}>
                    TVA: {formatAmount(filteredDepenses.reduce((sum, d) => sum + Number(d.montant_tva), 0))} €
                  </td>
                  <td colSpan={5}></td>
                </tr>
              </tbody>
            </table>
          </TableContainer>
        )}

        {/* Pagination */}
        <Box sx={{ mt: 2, display: "flex", justifyContent: "center", gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            size="small"
          >
            Précédent
          </Button>
          <Button
            variant="outlined"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            size="small"
          >
            Suivant
          </Button>
        </Box>
      </Box>

      {/* Dialog pour ajouter/éditer une dépense */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingId ? "Modifier la dépense" : "Nouvelle dépense"}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Catégorie</InputLabel>
                  <Select
                    value={formData.categorie_id}
                    onChange={(e) =>
                      setFormData({ ...formData, categorie_id: e.target.value })
                    }
                    label="Catégorie"
                  >
                    {categories.map((categorie) => (
                      <MenuItem key={categorie.id} value={categorie.id}>
                        {categorie.nom}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Montant TTC"
                  type="number"
                  value={formData.montant}
                  onChange={(e) =>
                    setFormData({ ...formData, montant: e.target.value })
                  }
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {formData.monnaie === "EUR" ? (
                          <Euro size={20} />
                        ) : (
                          <DollarSign size={20} />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Monnaie</InputLabel>
                  <Select
                    value={formData.monnaie}
                    onChange={(e) =>
                      setFormData({ ...formData, monnaie: e.target.value })
                    }
                    label="Monnaie"
                  >
                    <MenuItem value="EUR">EUR (€)</MenuItem>
                    <MenuItem value="USD">USD ($)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Taux TVA (%)"
                  type="number"
                  value={formData.taux_tva}
                  onChange={(e) =>
                    setFormData({ ...formData, taux_tva: e.target.value })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.tva_deductible}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          tva_deductible: e.target.checked,
                        })
                      }
                    />
                  }
                  label="TVA déductible"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.est_recurrent}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          est_recurrent: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Dépense récurrente"
                />
              </Grid>
              {formData.est_recurrent && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Fréquence</InputLabel>
                    <Select
                      value={formData.frequence}
                      onChange={(e) =>
                        setFormData({ ...formData, frequence: e.target.value })
                      }
                      label="Fréquence"
                    >
                      <MenuItem value="1 semaine">1 semaine</MenuItem>
                      <MenuItem value="2 semaines">2 semaines</MenuItem>
                      <MenuItem value="1 mois">1 mois</MenuItem>
                      <MenuItem value="3 mois">3 mois</MenuItem>
                      <MenuItem value="6 mois">6 mois</MenuItem>
                      <MenuItem value="1 an">1 an</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date de début"
                  type="date"
                  value={formData.date_debut}
                  onChange={(e) =>
                    setFormData({ ...formData, date_debut: e.target.value })
                  }
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date de fin"
                  type="date"
                  value={formData.date_fin}
                  onChange={(e) =>
                    setFormData({ ...formData, date_fin: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingId ? "Mettre à jour" : "Créer"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default DepensesPage; 